import { authorizedFetch } from '../../../../../../../helpers/utils';
import {
  GetResponse,
  MessagingSettings,
} from '@weave/schema-gen-ts/dist/schemas/messaging/settings/v1/service.pb';
import { SettingType_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/settings/v1/settings.pb';
import {
  API,
  GetBusinessSettingsResponse,
  GetSchedulingAppointmentTypesResponse,
  GetSchedulingStaffsResponse,
  GetSchedulingResourcesResponse,
  GetSchedulesResponse,
} from '@weave/schema-gen-ts/dist/schemas/ai-settings/v1/service.pb';
import {
  AutoRulesService,
  ListRulesResponse,
} from '@weave/schema-gen-ts/dist/schemas/messaging/auto-rules/v1/service.pb';
import { AutoMessageType_Slug } from '@weave/schema-gen-ts/dist/schemas/messaging/auto-rules/v1/models.pb';

import { getLocationRegistrationStatusDetails } from '../../../../../../../apis/tendlc/api';
import { GetLocationRegistrationStatusDetailsResponse } from '@weave/schema-gen-ts/dist/schemas/tendlc/coordinator/v1/coordinator_service.pb';
import {
  CallRouteService,
  ListResponse,
  ReadResponse,
} from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import {
  TenantAPI,
  GetTenantsResponse,
} from '@weave/schema-gen-ts/dist/schemas/phone/tenant/tenant_api.pb';
import {
  PhoneRecordsAPI,
  GetHydratedCallRecordsResponse,
} from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/phone_records_api.pb';
import {
  CreatorKind,
  TaskSource,
  TimeWindow,
} from '@weave/schema-gen-ts/dist/schemas/task-center/shared/v1/enums.pb';
import {
  CountOfTasksReviewedByLocationResponse,
  TaskService,
} from '@weave/schema-gen-ts/dist/schemas/task-center/v1/task_service.pb';

export const VoiceAirHandoffQueryKeys = {
  baseQueryKey: ['voice-air-handoff'],
  getTruelarkBusinessId: (locationId: string) => [
    ...VoiceAirHandoffQueryKeys.baseQueryKey,
    'truelark-business-id',
    locationId,
  ],
  getTenDlcRegistrationStatus: (locationId: string) => [
    ...VoiceAirHandoffQueryKeys.baseQueryKey,
    'ten-dlc-registration-status',
    locationId,
  ],
  getBusinessSettings: (locationId: string) => [
    ...VoiceAirHandoffQueryKeys.baseQueryKey,
    'bridgeline-business-settings',
    locationId,
  ],
  getAutoRules: (locationId: string, autoMessageType: string) => [
    ...VoiceAirHandoffQueryKeys.baseQueryKey,
    'auto-rules',
    locationId,
    autoMessageType,
  ],
  getSchedulingAppointmentTypes: (locationId: string) => [
    ...VoiceAirHandoffQueryKeys.baseQueryKey,
    'scheduling-appointment-types',
    locationId,
  ],
  getSchedulingStaffs: (locationId: string) => [
    ...VoiceAirHandoffQueryKeys.baseQueryKey,
    'scheduling-staffs',
    locationId,
  ],
  getSchedulingResources: (locationId: string) => [
    ...VoiceAirHandoffQueryKeys.baseQueryKey,
    'scheduling-resources',
    locationId,
  ],
  getSchedulingSchedules: (locationId: string) => [
    ...VoiceAirHandoffQueryKeys.baseQueryKey,
    'scheduling-schedules',
    locationId,
  ],
  getCallRoutesList: (tenantId: string) => [
    ...VoiceAirHandoffQueryKeys.baseQueryKey,
    'call-routes-list',
    tenantId,
  ],
  getCallRouteRead: (callRouteId: string) => [
    ...VoiceAirHandoffQueryKeys.baseQueryKey,
    'call-route-read',
    callRouteId,
  ],
  getPhoneTenants: (orgId: string) => [
    ...VoiceAirHandoffQueryKeys.baseQueryKey,
    'phone-tenants',
    orgId,
  ],
  getVoiceAiTasksReviewedCount: (locationId: string) => [
    ...VoiceAirHandoffQueryKeys.baseQueryKey,
    'voice-ai-tasks-reviewed-count',
    locationId,
  ],
  getHydratedCallRecords: (locationId: string) => [
    ...VoiceAirHandoffQueryKeys.baseQueryKey,
    'hydrated-call-records',
    locationId,
  ],
};

const MINUTE_MS = 60 * 1000;

export const getTruelarkBusinessIdQueryOptions = (
  locationId: string,
  orgId?: string
) => ({
  queryKey: VoiceAirHandoffQueryKeys.getTruelarkBusinessId(locationId),
  queryFn: async (): Promise<GetResponse> => {
    return await MessagingSettings.Get(authorizedFetch(), {
      orgId: orgId ?? '',
      locationId,
      settingType: SettingType_Enum['TRUELARK_LOCATION_SETTING'],
    });
  },
  cacheTime: 30 * MINUTE_MS,
  staleTime: 30 * MINUTE_MS,
});

export const getTenDlcRegistrationStatusQueryOptions = (locationId: string) => ({
  queryKey: VoiceAirHandoffQueryKeys.getTenDlcRegistrationStatus(locationId),
  queryFn: async (): Promise<GetLocationRegistrationStatusDetailsResponse> => {
    return await getLocationRegistrationStatusDetails({ locationId });
  },
  cacheTime: 30 * MINUTE_MS,
  staleTime: 30 * MINUTE_MS,
});

export const getBusinessSettingsQueryOptions = (locationId: string, orgId?: string) => ({
  queryKey: VoiceAirHandoffQueryKeys.getBusinessSettings(locationId),
  queryFn: async (): Promise<GetBusinessSettingsResponse> => {
    return await API.GetBusinessSettings(authorizedFetch(), {
      locationId,
      orgId: orgId ?? '',
    });
  },
  cacheTime: 30 * MINUTE_MS,
  staleTime: 30 * MINUTE_MS,
});

export const getAutoRulesQueryOptions = (
  locationId: string,
  autoMessageType: AutoMessageType_Slug,
  orgId?: string
) => ({
  queryKey: VoiceAirHandoffQueryKeys.getAutoRules(locationId, autoMessageType),
  queryFn: async (): Promise<ListRulesResponse> => {
    return await AutoRulesService.ListRules(authorizedFetch(), {
      orgId: orgId ?? '',
      businessGroups: [locationId],
      skipFilterValidation: true,
      summaryOnly: true,
      autoMessageType,
    });
  },
  cacheTime: 30 * MINUTE_MS,
  staleTime: 30 * MINUTE_MS,
});

export const getSchedulingAppointmentTypesQueryOptions = (
  locationId: string,
  orgId?: string
) => ({
  queryKey: VoiceAirHandoffQueryKeys.getSchedulingAppointmentTypes(locationId),
  queryFn: async (): Promise<GetSchedulingAppointmentTypesResponse> => {
    return await API.GetSchedulingAppointmentTypes(authorizedFetch(), {
      locationId,
      orgId: orgId ?? '',
    });
  },
  cacheTime: 30 * MINUTE_MS,
  staleTime: 30 * MINUTE_MS,
});

export const getSchedulingStaffsQueryOptions = (locationId: string, orgId?: string) => ({
  queryKey: VoiceAirHandoffQueryKeys.getSchedulingStaffs(locationId),
  queryFn: async (): Promise<GetSchedulingStaffsResponse> => {
    return await API.GetSchedulingStaffs(authorizedFetch(), {
      locationId,
      orgId: orgId ?? '',
    });
  },
  cacheTime: 30 * MINUTE_MS,
  staleTime: 30 * MINUTE_MS,
});

export const getSchedulingResourcesQueryOptions = (
  locationId: string,
  orgId?: string
) => ({
  queryKey: VoiceAirHandoffQueryKeys.getSchedulingResources(locationId),
  queryFn: async (): Promise<GetSchedulingResourcesResponse> => {
    return await API.GetSchedulingResources(authorizedFetch(), {
      locationId,
      orgId: orgId ?? '',
    });
  },
  cacheTime: 30 * MINUTE_MS,
  staleTime: 30 * MINUTE_MS,
});

export const getSchedulingSchedulesQueryOptions = (
  locationId: string,
  orgId?: string
) => ({
  queryKey: VoiceAirHandoffQueryKeys.getSchedulingSchedules(locationId),
  queryFn: async (): Promise<GetSchedulesResponse> => {
    return await API.GetSchedulingSchedules(authorizedFetch(), {
      locationId,
      orgId: orgId ?? '',
    });
  },
  cacheTime: 30 * MINUTE_MS,
  staleTime: 30 * MINUTE_MS,
});

export const getCallRoutesListQueryOptions = (tenantId: string) => ({
  queryKey: VoiceAirHandoffQueryKeys.getCallRoutesList(tenantId),
  queryFn: async (): Promise<ListResponse> => {
    return await CallRouteService.List(authorizedFetch(), { tenantId });
  },
  cacheTime: 5 * MINUTE_MS,
  staleTime: 5 * MINUTE_MS,
});

export const getCallRouteReadQueryOptions = (callRouteId: string) => ({
  queryKey: VoiceAirHandoffQueryKeys.getCallRouteRead(callRouteId),
  queryFn: async (): Promise<ReadResponse> => {
    return await CallRouteService.Read(authorizedFetch(), { callRouteId });
  },
  cacheTime: 5 * MINUTE_MS,
  staleTime: 5 * MINUTE_MS,
});

export const getPhoneTenantsQueryOptions = (orgId: string) => ({
  queryKey: VoiceAirHandoffQueryKeys.getPhoneTenants(orgId),
  queryFn: async (): Promise<GetTenantsResponse> => {
    return await TenantAPI.GetTenants(authorizedFetch(), { orgId });
  },
  cacheTime: 30 * MINUTE_MS,
  staleTime: 30 * MINUTE_MS,
});

export const getHydratedCallRecordsQueryOptions = (
  locationId: string,
  startTime: string,
  endTime: string
) => ({
  queryKey: VoiceAirHandoffQueryKeys.getHydratedCallRecords(locationId),
  queryFn: async (): Promise<GetHydratedCallRecordsResponse> => {
    return await PhoneRecordsAPI.GetHydratedCallRecords(authorizedFetch(), {
      locationIds: [locationId],
      limit: 10,
      filters: {
        aiReceptionistHandled: true,
        startTime,
        endTime,
      },
    });
  },
  cacheTime: 5 * MINUTE_MS,
  staleTime: 5 * MINUTE_MS,
});

export const getVoiceAiTasksReviewedCountQueryOptions = (locationId: string) => ({
  queryKey: VoiceAirHandoffQueryKeys.getVoiceAiTasksReviewedCount(locationId),
  queryFn: async (): Promise<CountOfTasksReviewedByLocationResponse> => {
    return await TaskService.CountOfTasksReviewedByLocation(authorizedFetch(), {
      locationId: [locationId],
      source: TaskSource.SOURCE_CALL_INTELLIGENCE,
      creatorType: CreatorKind.CREATOR_KIND_AIR,
      timeWindow: TimeWindow.TIME_WINDOW_LAST_7_DAYS,
    });
  },
  cacheTime: 5 * MINUTE_MS,
  staleTime: 5 * MINUTE_MS,
});
